import {CannedMessage} from '../components/SendCannedMessages/types'
import React from 'react'

export interface RowingTabIndexProps {
    filter: boolean,
    nodes?: CannedMessage[] | string[],
    listenToDOMChanges?: boolean
    selectAllFocusableElements?: boolean
}

const mutationConfig = {
    attributes: true,
    childList: true,
    subtree: true,
}

// eslint-disable-next-line max-len
const allFocusableElements = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])'

export const useRovingTabIndex = (props: RowingTabIndexProps) => {
    const {
        filter = false, nodes, listenToDOMChanges = false, selectAllFocusableElements = false,
    } = props

    const ref = React.useRef<HTMLDivElement>(null)

    const [mutationRecords, setMutationRecords] = React.useState<Array<MutationRecord>>([])

    const domChangesDetected = (records: Array<MutationRecord>) => {
        setMutationRecords(records)
    }

    React.useEffect(() => {
        if (listenToDOMChanges) {
            const observer = new MutationObserver(domChangesDetected)

            if (ref.current) {
                observer.observe(ref.current, mutationConfig)
            }

            return () => {
                observer.disconnect()
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {}
    }, [])

    React.useEffect(() => {
        let list: HTMLElement[] = [...ref.current?.querySelectorAll<HTMLElement>(selectAllFocusableElements ? allFocusableElements : '[tabIndex]') ?? []]
        let index = 0

        // get only visible elements
        if (selectAllFocusableElements && list) {
            list = [...list].filter((item: HTMLElement) => window.getComputedStyle(item).visibility !== 'hidden')

            if (list && list.length > 0) {
                const focusedElementIndex = [...list].findIndex((item) => item.isEqualNode(document.activeElement))
                if (focusedElementIndex !== -1) {
                    index = focusedElementIndex
                }
            }
        }

        if (list?.length) {
            if (filter) {
                Array.from(list).map((item) => {
                    item.tabIndex = -1
                    return item
                })
            } else {
                const found = Array.from(list).findIndex((item) => item.tabIndex === 0)
                if (found !== -1) {
                    index = found
                }

                // when listen to Dom changes we shoudl not change focus each time
                if (list[index] && (!listenToDOMChanges || mutationRecords.length === 0)) {
                    list[index].focus()
                }
            }
        }

        const focusDown = (arrowKey?: boolean): void => {
            if (list?.length) {
                if (arrowKey && list[index].nodeName === 'TEXTAREA') {
                    return // Skip arrow key focus if textarea
                }

                Array.from(list).map((item) => {
                    item.tabIndex = -1
                    return item
                })

                index = index === list.length - 1 ? 0 : index + 1
                list[index].tabIndex = 0
                list[index].focus()
            }
        }

        const focusFirst = (): void => {
            if (list?.length) {
                Array.from(list).map((item) => {
                    item.tabIndex = -1
                    return item
                })
                index = 0
                list[index].tabIndex = 0
                list[index].focus()
            }
        }

        const focusUp = (arrowKey?: boolean): void => {
            if (list?.length) {
                if (arrowKey && list[index].nodeName === 'TEXTAREA') {
                    return // Skip arrow key focus if textarea
                }

                Array.from(list).map((item) => {
                    item.tabIndex = -1
                    return item
                })
                index = index === 0 ? list.length - 1 : index - 1
                list[index].tabIndex = 0
                list[index].focus()
            }
        }

        const onKeyDown = (e: KeyboardEvent): void => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    if (ref.current?.contains(e.target as Node)) {
                        focusDown(true)
                    } else {
                        // Focus first element if tab was pressed outside list eg. from Send textarea
                        focusFirst()
                    }
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    focusUp(true)
                    break
                case 'Tab':
                    e.preventDefault()
                    if (ref.current?.contains(e.target as Node)) {
                        if (e.shiftKey) {
                            focusUp()
                        } else {
                            focusDown()
                        }
                    } else {
                        // Focus first element if tab was pressed outside list eg. from Send textarea
                        focusFirst()
                    }
                    break
                default:
                    break
            }
        }

        window.addEventListener('keydown', onKeyDown, {capture: true})

        return () => window.removeEventListener('keydown', onKeyDown, {capture: true})
    }, [ref.current, filter, nodes, mutationRecords])

    return ref
}
